import { asset } from "./assets";

interface LocalizedText {
  en: string;
  hi: string;
}

// Not exported: nothing outside this module needs the tone union directly,
// only the tiles that carry it (fallow's dead-code check flags an exported
// type with no importer).
type TileTone = "teal" | "mint" | "sun" | "peach" | "sage" | "stone";

// Not exported: nothing outside this module needs the per-tile shape, only
// the assembled RESOURCE_TILES value below (fallow's dead-code check flags
// an exported type with no importer).
interface ResourceTile {
  title: LocalizedText;
  sub: LocalizedText;
  href: string;
  tone: TileTone;
  external?: boolean;
}

/**
 * `/resources` (and `/resources-HI`): the six-tile carousel, verbatim
 * sub-labels from the old site. The old site's Hindi rendering of this page
 * carried the same English tile text (no distinct Hindi copy exists for
 * these captions), so `hi` mirrors `en` for every tile here.
 */
export const RESOURCE_TILES: readonly ResourceTile[] = [
  {
    title: { en: "Audio & Video", hi: "Audio & Video" },
    sub: {
      en: "YouTube, Podcast, Facebook",
      hi: "YouTube, Podcast, Facebook",
    },
    href: "/resources/audio-video",
    tone: "teal",
  },
  {
    title: { en: "Slides Reading", hi: "Slides Reading" },
    sub: {
      en: "Spiritual Concepts Know more",
      hi: "Spiritual Concepts Know more",
    },
    href: "/resources/slide-reading",
    tone: "mint",
  },
  {
    title: { en: "Scientific Research", hi: "Scientific Research" },
    sub: {
      en: "Meditation & Alpha Waves",
      hi: "Meditation & Alpha Waves",
    },
    href: "/resources/scientific-research",
    tone: "sun",
  },
  {
    title: { en: "Newsletter / Magazine", hi: "Newsletter / Magazine" },
    sub: {
      en: "Divinity with small booklets",
      hi: "Divinity with small booklets",
    },
    href: "/resources/newsletter-magazine",
    tone: "peach",
  },
  {
    title: { en: "Prayers & Music", hi: "Prayers & Music" },
    sub: {
      en: "Daily Prayer & Meditation Music",
      hi: "Daily Prayer & Meditation Music",
    },
    href: "/resources/prayer-music",
    tone: "sage",
  },
  {
    title: { en: "Divine Quotes", hi: "Divine Quotes" },
    sub: { en: "Enlighten divine words", hi: "Enlighten divine words" },
    href: "https://www.instagram.com/naam.deo/?hl=en",
    tone: "stone",
    external: true,
  },
] as const;

interface ResourceLink {
  label: string;
  href: string;
}

interface LinkGroup {
  heading: string;
  links: readonly ResourceLink[];
}

/**
 * `/resources/audio-video`: the verbatim link set, grouped under the three
 * headings the old site's own icon carousel used (YouTube, PodCast,
 * Facebook). English only - the old site had no Hindi rendering of this
 * page.
 */
export const AUDIO_VIDEO_GROUPS: readonly LinkGroup[] = [
  {
    heading: "YouTube",
    links: [
      {
        label: "Knowledge Sharing",
        href: "http://www.youtube.com/vihangamyogasingapore",
      },
      {
        label: "Divine Conscious Messages from Naamdeo ji",
        href: "https://www.youtube.com/c/NaamDeo/videos",
      },
      {
        label: "Discourses / Amritvani / Divyavani",
        href: "https://www.youtube.com/user/vihangamyoga/videos",
      },
    ],
  },
  {
    heading: "PodCast",
    links: [
      {
        label: "Global Online Forum (VYGOF)",
        href: "https://podcasts.apple.com/us/podcast/vihangam-yoga-global-online-forum/id1508916884",
      },
    ],
  },
  {
    heading: "Facebook",
    links: [
      {
        label: "VY Singapore",
        href: "https://www.facebook.com/vihangamyogasingapore",
      },
      { label: "Naamdeo ji", href: "https://www.facebook.com/TheNaamdeo" },
      {
        label: "VY India / SantPravar",
        href: "https://www.facebook.com/santpravar",
      },
    ],
  },
] as const;

interface ConceptTile {
  title: string;
  desc: string;
  href: string;
  tone: TileTone;
  external?: boolean;
}

/**
 * `/resources/slide-reading`: the six concept tiles, verbatim titles and
 * descriptions. The old site renders these card titles in upper case as
 * literal content (not a CSS transform), so they are stored verbatim in
 * upper case here. Meditation Practice opens the practice-guide PDF; the
 * other five open the slide gallery at `/resources/slides`. English only -
 * no Hindi twin.
 */
export const SLIDE_READING_TILES: readonly ConceptTile[] = [
  {
    title: "MEDITATION PRACTICE",
    desc: "The steps to 30-minutes practice session includes couple of Breathing Exercises & Vihangam Meditation",
    href: asset(
      "https://site-hjx4qqt6.wsecdn1.websitecdn.com/uploads/EB26FCBE71769ED4.pdf?v=250703010154",
    ),
    tone: "teal",
    external: true,
  },
  {
    title: "WHAT IS YOGA",
    desc: "Yoga is union of Soul with Supreme Soul!. How Vihangam Yoga is unique and different from others?",
    href: "/resources/slides",
    tone: "mint",
  },
  {
    title: "GURU",
    desc: "The importance, quality, type and existence of Guru. Who is the TRUE Master? Is there an Eternal Master?",
    href: "/resources/slides",
    tone: "sun",
  },
  {
    title: "KARMA",
    desc: "The meaning & types of Karma at different dimensions. Karma are the cause of vicious cycle of death-birth.",
    href: "/resources/slides",
    tone: "peach",
  },
  {
    title: "CHAKRAS & DOORS",
    desc: "Types of Pranas, Chakras &Secret Doors available in our body are the source of revitalization. Reactivate them.",
    href: "/resources/slides",
    tone: "sage",
  },
  {
    title: "SWARVED COUPLETS",
    desc: "Various spiritual concepts & principles mentioned by Swamiji in Swarved - the encyclopedia of spirituality.",
    href: "/resources/slides",
    tone: "stone",
  },
] as const;

interface Slide {
  src: string;
  caption: string;
}

/**
 * `/resources/slides`: the 33 concept-slide images, in the old site's own
 * carousel order, verbatim captions (one duplicate caption - "Types of
 * KARMA - Dimension 1" appears on two different slides - matches the old
 * site itself). English only - no Hindi twin.
 */
export const SLIDES: readonly Slide[] = [
  {
    src: asset(
      "https://site-hjx4qqt6.wsecdn1.websitecdn.com/uploads/608-291/F0DC60240063AB5E.png?v=250703010154",
    ),
    caption: "What is Yoga",
  },
  {
    src: asset(
      "https://site-hjx4qqt6.wsecdn1.websitecdn.com/uploads/608-291/DF5C4ACD0FEF89CC.png?v=250703010154",
    ),
    caption: "Hath - Raj - Vihangam Yoga",
  },
  {
    src: asset(
      "https://site-hjx4qqt6.wsecdn1.websitecdn.com/uploads/608-290/37D753A0EFA3B7CC.png?v=250703010154",
    ),
    caption: "What is KARMA",
  },
  {
    src: asset(
      "https://site-hjx4qqt6.wsecdn1.websitecdn.com/uploads/608-290/504344B68054989C.png?v=250703010154",
    ),
    caption: "Types of KARMA - Dimension 1",
  },
  {
    src: asset(
      "https://site-hjx4qqt6.wsecdn1.websitecdn.com/uploads/608-290/0D4BB4B1C57F72CE.png?v=250703010154",
    ),
    caption: "Types of KARMA - Dimension 1",
  },
  {
    src: asset(
      "https://site-hjx4qqt6.wsecdn1.websitecdn.com/uploads/1083-513/5725A39127D77C9E.png?v=250703010154",
    ),
    caption: "Savior from KARMA",
  },
  {
    src: asset(
      "https://site-hjx4qqt6.wsecdn1.websitecdn.com/uploads/1083-513/DCE29CE63116AF15.png?v=250703010154",
    ),
    caption: "GURU - Generic",
  },
  {
    src: asset(
      "https://site-hjx4qqt6.wsecdn1.websitecdn.com/uploads/1083-513/04716A45626D1E12.png?v=250703010154",
    ),
    caption: "TYPES OF SADGURU",
  },
  {
    src: asset(
      "https://site-hjx4qqt6.wsecdn1.websitecdn.com/uploads/1083-513/CED4560517A01D20.png?v=250703010154",
    ),
    caption: "SADGURU SADAFALDEO JI MAHARAJ Part 1",
  },
  {
    src: asset(
      "https://site-hjx4qqt6.wsecdn1.websitecdn.com/uploads/1083-513/54D0D2396C56177A.png?v=250703010154",
    ),
    caption: "SADGURU SADAFALDEO JI MAHARAJ Part 2",
  },
  {
    src: asset(
      "https://site-hjx4qqt6.wsecdn1.websitecdn.com/uploads/1083-513/152E12E122CDE385.png?v=250703010154",
    ),
    caption: "SADGURU SADAFALDEO JI MAHARAJ Part 3",
  },
  {
    src: asset(
      "https://site-hjx4qqt6.wsecdn1.websitecdn.com/uploads/1083-513/4EA728CCA6B6FC09.png?v=250703010154",
    ),
    caption: "SADGURU SADAFALDEO JI MAHARAJ Part 4",
  },
  {
    src: asset(
      "https://site-hjx4qqt6.wsecdn1.websitecdn.com/uploads/1083-513/593B2B3A052B2267.png?v=250703010154",
    ),
    caption: "SADGURU SADAFALDEO JI MAHARAJ Part 5",
  },
  {
    src: asset(
      "https://site-hjx4qqt6.wsecdn1.websitecdn.com/uploads/1083-513/3A149A20A75A1DC0.png?v=250703010154",
    ),
    caption: "SADGURU DHARMACHANDRADEO JI MAHARAJ",
  },
  {
    src: asset(
      "https://site-hjx4qqt6.wsecdn1.websitecdn.com/uploads/1083-513/F4B79DF910737939.png?v=250703010154",
    ),
    caption: "SADGURU SWATANTRADEO JI MAHARAJ Part 1",
  },
  {
    src: asset(
      "https://site-hjx4qqt6.wsecdn1.websitecdn.com/uploads/1083-513/A53B03EEAB341AA6.png?v=250703010154",
    ),
    caption: "SADGURU SWATANTRADEO JI MAHARAJ Part 2",
  },
  {
    src: asset(
      "https://site-hjx4qqt6.wsecdn1.websitecdn.com/uploads/1083-513/0F98C039B724A853.png?v=250703010154",
    ),
    caption: "SADGURU SWATANTRADEO JI MAHARAJ Part 3",
  },
  {
    src: asset(
      "https://site-hjx4qqt6.wsecdn1.websitecdn.com/uploads/1083-513/EAC5684E01EF38C7.png?v=250703010154",
    ),
    caption: "SADGURU SWATANTRADEO JI MAHARAJ Part 4",
  },
  {
    src: asset(
      "https://site-hjx4qqt6.wsecdn1.websitecdn.com/uploads/608-288/CDBAE38D3021F3E2.png?v=250703010154",
    ),
    caption: "MIND. INTELLECT. MEMORY. EGO",
  },
  {
    src: asset(
      "https://site-hjx4qqt6.wsecdn1.websitecdn.com/uploads/608-288/63C41A415A23BA66.png?v=250703010154",
    ),
    caption: "SENSE ORGANS",
  },
  {
    src: asset(
      "https://site-hjx4qqt6.wsecdn1.websitecdn.com/uploads/608-288/5372C84521BA060C.png?v=250703010154",
    ),
    caption: "CHAKRAS",
  },
  {
    src: asset(
      "https://site-hjx4qqt6.wsecdn1.websitecdn.com/uploads/608-288/C4175C4342189500.png?v=250703010154",
    ),
    caption: "ETERNAL ITEMS",
  },
  {
    src: asset(
      "https://site-hjx4qqt6.wsecdn1.websitecdn.com/uploads/608-288/3142ACCB856306D9.png?v=250703010154",
    ),
    caption: "TYPE OF KNOWLEDGE",
  },
  {
    src: asset(
      "https://site-hjx4qqt6.wsecdn1.websitecdn.com/uploads/608-288/B855F0141EA19544.png?v=250703010154",
    ),
    caption: "SAT. RAJ. TAM.",
  },
  {
    src: asset(
      "https://site-hjx4qqt6.wsecdn1.websitecdn.com/uploads/608-288/0A808869AC9837AE.png?v=250703010154",
    ),
    caption: "PANCHKOSH",
  },
  {
    src: asset(
      "https://site-hjx4qqt6.wsecdn1.websitecdn.com/uploads/608-288/3AC58331E2E636DD.png?v=250703010154",
    ),
    caption: "OPERATINNG SYSTEM",
  },
  {
    src: asset(
      "https://site-hjx4qqt6.wsecdn1.websitecdn.com/uploads/608-288/2C4A1E0822D7E62B.png?v=250703010154",
    ),
    caption: "THE SECRET 10th & 11th DOORS",
  },
  {
    src: asset(
      "https://site-hjx4qqt6.wsecdn1.websitecdn.com/uploads/608-288/C5C7D90B761E975D.png?v=250703010154",
    ),
    caption: "TYPE OF VOICE",
  },
  {
    src: asset(
      "https://site-hjx4qqt6.wsecdn1.websitecdn.com/uploads/608-288/D3C0AA9344FF34AE.png?v=250703010154",
    ),
    caption: "5 STAGES OF MEDITATION",
  },
  {
    src: asset(
      "https://site-hjx4qqt6.wsecdn1.websitecdn.com/uploads/608-288/57572F304DAEF276.png?v=250703010154",
    ),
    caption: "VIHANGAM - ALALA BIRD",
  },
  {
    src: asset(
      "https://site-hjx4qqt6.wsecdn1.websitecdn.com/uploads/608-288/0F987FB827A63B5A.png?v=250703010154",
    ),
    caption: "ANAHAD - MUSIC",
  },
  {
    src: asset(
      "https://site-hjx4qqt6.wsecdn1.websitecdn.com/uploads/608-287/75C5B88D8006F20E.png?v=250703010154",
    ),
    caption: "SWARVED - the encyclopedia of spirituality",
  },
  {
    src: asset(
      "https://site-hjx4qqt6.wsecdn1.websitecdn.com/uploads/608-287/5136C6CF547A1B3E.png?v=250703010154",
    ),
    caption: "SWARVED - CONTENTS",
  },
] as const;

interface Newsletter {
  title: string;
  blurb: string;
  href: string;
}

/**
 * `/resources/newsletter-magazine`: the three 2018 newsletter PDFs,
 * verbatim titles and content blurbs. English only - no Hindi twin.
 */
export const NEWSLETTERS: readonly Newsletter[] = [
  {
    title: "VYSG Jan 2018 Newsletter",
    blurb:
      "The virtue of consciousness, Importance of Service, Diet Chart for Diabetics, Bhujangasana",
    href: asset(
      "https://site-hjx4qqt6.wsecdn1.websitecdn.com/uploads/FA26DEF0263E47FD.pdf?v=250703010154",
    ),
  },
  {
    title: "VYSG Mar 2018 Newsletter",
    blurb:
      "Platforms of Knowledge, Importance of Prayer, (Health) Digestive Energy, Gomukhasana",
    href: asset(
      "https://site-hjx4qqt6.wsecdn1.websitecdn.com/uploads/3EBBD38474DDA34B.pdf?v=250703010154",
    ),
  },
  {
    title: "VYSG Apr 2018 Newsletter",
    blurb:
      "Inner Journey, Devotion, Sadguru Sadafaldeo: A Brief Introduction, (Health) Blood Pressure, Vajrasana",
    href: asset(
      "https://site-hjx4qqt6.wsecdn1.websitecdn.com/uploads/10961885107A0B39.pdf?v=250703010154",
    ),
  },
] as const;

/**
 * The "Vihangam Light (Magazine)" block on `/resources/newsletter-magazine`:
 * verbatim Jan 2020 description, its Download PDF, and the page-level
 * Subscribe button (the old site pointed both at a contact form).
 */
export const MAGAZINE = {
  title: "Vihangam Light (Magazine)",
  edition: "Jan 2020",
  description:
    "Vihangam Light is aimed at helping people discover their inner light of truth, Vihangam Light is a unique wellness magazine that covers a wide range of wellness topics ranging from physical fitness to yoga and pranayam techniques to providing solutions to emotional issues and finally spreading the light of spiritual knowledge. The monthly editions are complete with articles, personal experiences, wellness tips, lifestyle and diet recommendations and unique travel stories. From working women, to young adults, to health conscious individuals, to the spiritually inclined, to corporate professionals, to seekers – Vihangam Light has something for everyone. The magazine is currently published in English and has subscribers from across the globe.",
  href: asset(
    "https://site-hjx4qqt6.wsecdn1.websitecdn.com/uploads/1476A716728DDF53.pdf?v=250703010154",
  ),
  subscribeHref: "/contact",
};
